import {
    ArrowLeft,
    Save
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const UnitAddWithBuildingSelect = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedPropertyOwnerId, setSelectedPropertyOwnerId] = useState('');
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [filteredBuildings, setFilteredBuildings] = useState([]);

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me')
  );

  // RMC 사용자 여부 확인
  const isRMCUser = userInfo?.role === 'rental_management_company' || 
                    userInfo?.role === 'rmc_admin' || 
                    userInfo?.role === 'rmc_staff';

  // 임대사업자 목록 가져오기 (RMC 사용자인 경우에만)
  const fetchPropertyOwners = useCallback(async () => {
    if (isRMCUser) {
      try {
        const response = await api.get('/rmc/property-owners');
        console.log('임대사업자 목록 응답:', response);
        
        if (response && Array.isArray(response)) {
          setPropertyOwners(response);
          console.log('✅ 임대사업자 목록 설정 완료:', response.length, '개');
        } else {
          console.log('⚠️ 임대사업자 목록 데이터가 없거나 배열이 아닙니다');
          setPropertyOwners([]);
        }
      } catch (error) {
        console.error('임대사업자 목록 조회 오류:', error);
        setPropertyOwners([]);
      }
    }
  }, [isRMCUser]);

  useEffect(() => {
    fetchPropertyOwners();
  }, [fetchPropertyOwners]);

  // 건물 목록 가져오기 (사용자 역할에 따라 다른 API 사용)
  const { data: buildings, isLoading: buildingsLoading } = useQuery(
    ['buildings', userInfo?.role],
    async () => {
      if (userInfo?.role === 'rental_management_company') {
        // RMC 사용자는 RMC 건물 API 사용
        const response = await api.get('/rmc/buildings');
        console.log('🔍 RMC 건물 API 응답:', response);
        console.log('🔍 응답 타입:', typeof response);
        console.log('🔍 배열 여부:', Array.isArray(response));
        
        // RMC API는 {buildings: [...]} 형태로 응답할 수 있음
        if (response && response.buildings) {
          console.log('🔍 response.buildings 사용:', response.buildings);
          return response.buildings;
        } else if (Array.isArray(response)) {
          console.log('🔍 직접 배열 사용:', response);
          return response;
        } else {
          console.log('🔍 빈 배열 반환');
          return [];
        }
      } else {
        // 일반 사용자는 일반 건물 API 사용
        const response = await api.get('/buildings');
        return Array.isArray(response) ? response : response?.data || [];
      }
    },
    {
      enabled: !!userInfo, // 사용자 정보가 로드된 후에만 실행
    }
  );

  // 임대사업자 선택 시 건물 목록 필터링
  useEffect(() => {
    console.log('🔍 필터링 로직 실행:', {
      buildings: buildings,
      selectedPropertyOwnerId: selectedPropertyOwnerId,
      userRole: userInfo?.role
    });
    
    if (buildings && selectedPropertyOwnerId) {
      console.log('🔍 건물 데이터 구조 확인:', buildings[0]);
      console.log('🔍 건물 데이터 전체 키:', Object.keys(buildings[0]));
      console.log('🔍 선택된 임대사업자 ID:', selectedPropertyOwnerId, typeof selectedPropertyOwnerId);
      
      const filtered = buildings.filter(building => {
        const buildingOwnerId = building.property_owner?.id;
        const match = buildingOwnerId === parseInt(selectedPropertyOwnerId);
        
        console.log('🔍 건물 비교:', {
          buildingId: building.id,
          buildingName: building.name,
          buildingPropertyOwnerId: buildingOwnerId,
          buildingPropertyOwnerIdType: typeof buildingOwnerId,
          selectedPropertyOwnerId: parseInt(selectedPropertyOwnerId),
          selectedPropertyOwnerIdType: typeof parseInt(selectedPropertyOwnerId),
          match: match,
          propertyOwner: building.property_owner
        });
        return match;
      });
      setFilteredBuildings(filtered);
      console.log('✅ 필터링된 건물 목록:', filtered);
    } else if (buildings && userInfo?.role !== 'rental_management_company') {
      // 일반 사용자는 모든 건물 표시
      setFilteredBuildings(buildings);
    } else {
      setFilteredBuildings([]);
    }
    
    // 임대사업자 변경 시 건물 선택 초기화
    setSelectedBuildingId('');
  }, [buildings, selectedPropertyOwnerId, userInfo?.role]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch
  } = useForm({
    defaultValues: {
      unit_number: '',
      floor: '',
      area: '',
      unit_type: '',
      status: 'available',
      standard_monthly_rent: '',
      standard_deposit: '',
      standard_maintenance_fee: '',
      vacancy_electric_fee: '',
      vacancy_water_fee: '',
      vacancy_gas_fee: '',
      vacancy_common_fee: '',
      vacancy_other_fee: '',
      vacancy_cost_payer: 'property_owner',
      notes: ''
    }
  });

  // 세대 생성 뮤테이션
  const createUnitMutation = useMutation(
    (data) => api.post(`/buildings/${selectedBuildingId}/units`, data),
    {
      onSuccess: () => {
        toast.success('세대가 성공적으로 등록되었습니다.');
        queryClient.invalidateQueries('buildings');
        queryClient.invalidateQueries('all-units');
        navigate(userInfo?.role === 'rental_management_company' ? '/rmc/units' : '/units');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '세대 등록에 실패했습니다.');
      }
    }
  );

  const onSubmit = (data) => {
    if (!selectedBuildingId) {
      toast.error('건물을 선택해주세요.');
      return;
    }

    const unitData = {
      ...data,
      standard_monthly_rent: data.standard_monthly_rent ? parseInt(data.standard_monthly_rent) : null,
      standard_deposit: data.standard_deposit ? parseInt(data.standard_deposit) : null,
      standard_maintenance_fee: data.standard_maintenance_fee ? parseInt(data.standard_maintenance_fee) : null,
      vacancy_electric_fee: data.vacancy_electric_fee ? parseInt(data.vacancy_electric_fee) : 0,
      vacancy_water_fee: data.vacancy_water_fee ? parseInt(data.vacancy_water_fee) : 0,
      vacancy_gas_fee: data.vacancy_gas_fee ? parseInt(data.vacancy_gas_fee) : 0,
      vacancy_common_fee: data.vacancy_common_fee ? parseInt(data.vacancy_common_fee) : 0,
      vacancy_other_fee: data.vacancy_other_fee ? parseInt(data.vacancy_other_fee) : 0,
      vacancy_cost_payer: data.vacancy_cost_payer || 'property_owner',
      floor: data.floor ? parseInt(data.floor) : null,
      area: data.area ? parseFloat(data.area) : null
    };

    createUnitMutation.mutate(unitData);
  };

  if (buildingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(userInfo?.role === 'rental_management_company' ? '/rmc/units' : '/units')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            뒤로가기
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">세대 추가</h1>
            <p className="mt-1 text-sm text-gray-500">
              새로운 세대를 등록하세요
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 건물 선택 */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">건물 선택</h2>
          <div className="grid grid-cols-1 gap-4">
            {/* RMC 사용자인 경우 임대사업자 선택 */}
            {isRMCUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  임대사업자 <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedPropertyOwnerId}
                  onChange={(e) => setSelectedPropertyOwnerId(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                >
                  <option value="">임대사업자를 선택하세요</option>
                  {propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name} ({owner.company_name || '개인'})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물 선택 <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedBuildingId}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                required
                disabled={userInfo?.role === 'rental_management_company' && !selectedPropertyOwnerId}
              >
                <option value="">건물을 선택하세요</option>
                {filteredBuildings?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name} - {building.address}
                  </option>
                ))}
              </select>
              {userInfo?.role === 'rental_management_company' && !selectedPropertyOwnerId && (
                <p className="mt-1 text-sm text-gray-500">먼저 임대사업자를 선택해주세요</p>
              )}
            </div>
          </div>
        </div>

        {/* 세대 기본 정보 */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">세대 기본 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                호수 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('unit_number', { required: '호수를 입력해주세요.' })}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 101호"
              />
              {errors.unit_number && (
                <p className="mt-1 text-sm text-red-600">{errors.unit_number.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                층수
              </label>
              <input
                type="number"
                {...register('floor')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                전용면적 (㎡)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('area')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 25.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                세대 타입
              </label>
              <select
                {...register('unit_type')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">선택하세요</option>
                <option value="원룸">원룸</option>
                <option value="1.5룸">1.5룸</option>
                <option value="투룸">투룸</option>
                <option value="쓰리룸 이상">쓰리룸 이상</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                상태
              </label>
              <select
                {...register('status')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="available">임대 가능</option>
                <option value="occupied">임대중</option>
                <option value="maintenance">정비중</option>
                <option value="reserved">예약중</option>
              </select>
            </div>


          </div>
        </div>

        {/* 기준임대료 */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">기준임대료</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기준월세 (원)
              </label>
              <input
                type="number"
                {...register('standard_monthly_rent')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 500000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기준보증금 (원)
              </label>
              <input
                type="number"
                {...register('standard_deposit')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 5000000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기준관리비 (원)
              </label>
              <input
                type="number"
                {...register('standard_maintenance_fee')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="예: 50000"
              />
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">공실 기본비용</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ['vacancy_electric_fee', '전기'],
              ['vacancy_water_fee', '수도'],
              ['vacancy_gas_fee', '가스'],
              ['vacancy_common_fee', '공용'],
              ['vacancy_other_fee', '기타']
            ].map(([field, label]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {label} (원/월)
                </label>
                <input
                  type="number"
                  min="0"
                  {...register(field)}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="0"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                부담 주체
              </label>
              <select
                {...register('vacancy_cost_payer')}
                className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="property_owner">임대사업자 부담</option>
                <option value="rental_manager">임대관리자 부담</option>
              </select>
            </div>
          </div>
        </div>

        {/* 특이사항 */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">특이사항</h2>
          <div>
            <textarea
              {...register('notes')}
              rows={4}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="세대에 대한 특이사항을 입력하세요..."
            />
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(userInfo?.role === 'rental_management_company' ? '/rmc/units' : '/units')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? '등록 중...' : '세대 등록'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UnitAddWithBuildingSelect;
